import { assign, fromPromise, setup } from 'xstate';
import { getProfileSuggestions, refreshProfileSuggestions } from './api';
import type {
  ProfileSuggestion,
  ProfileSuggestionJob,
  ProfileSuggestionsResponse,
  RefreshProfileSuggestionsResponse,
} from './types';

interface Context {
  providerId: number;
  featureEnabled: boolean;
  suggestions: ProfileSuggestion[];
  latestJob: ProfileSuggestionJob | null;
  canRefresh: boolean;
  nextRefreshAt: string | null;
  error: boolean;
  pollCount: number;
}

type Events = { type: 'LOAD'; providerId?: number } | { type: 'REFRESH' } | { type: 'POLL' };

type LoadInput = { providerId: number };

const outputOf = <T>(event: unknown): T => (event as { output: T }).output;
const MAX_POLL_ATTEMPTS = 12;

export const profileSuggestionsMachine = setup({
  actors: {
    getSuggestions: fromPromise(({ input }: { input: LoadInput }) =>
      getProfileSuggestions(input.providerId)
    ),
    refreshSuggestions: fromPromise(({ input }: { input: LoadInput }) =>
      refreshProfileSuggestions(input.providerId)
    ),
  },
  types: {
    context: {} as Context,
    events: {} as Events,
    input: {} as Partial<Context>,
  },
  guards: {
    shouldKeepPolling: ({ context, event }) => {
      const output = outputOf<ProfileSuggestionsResponse>(event);
      const status = output.latestJob?.status;
      return (
        context.pollCount + 1 < MAX_POLL_ATTEMPTS &&
        (status === 'PENDING' || status === 'STARTED' || status === 'PROCESSING')
      );
    },
    latestJobFailed: ({ event }) => {
      const output = outputOf<ProfileSuggestionsResponse>(event);
      return output.latestJob?.status === 'FAILED';
    },
    refreshAccepted: ({ event }) => outputOf<RefreshProfileSuggestionsResponse>(event).accepted,
  },
  actions: {
    prepareLoad: assign({
      providerId: ({ context, event }) =>
        event.type === 'LOAD' ? (event.providerId ?? context.providerId) : context.providerId,
      suggestions: [],
      latestJob: null,
      canRefresh: false,
      nextRefreshAt: null,
      pollCount: 0,
      error: false,
    }),
    setSuggestions: assign({
      featureEnabled: ({ event }) => outputOf<ProfileSuggestionsResponse>(event).featureEnabled,
      suggestions: ({ event }) => outputOf<ProfileSuggestionsResponse>(event).suggestions,
      latestJob: ({ event }) => outputOf<ProfileSuggestionsResponse>(event).latestJob,
      canRefresh: ({ event }) => outputOf<ProfileSuggestionsResponse>(event).canRefresh,
      nextRefreshAt: ({ event }) => outputOf<ProfileSuggestionsResponse>(event).nextRefreshAt,
      error: false,
    }),
    setRefreshAccepted: assign({
      latestJob: ({ event }) => outputOf<RefreshProfileSuggestionsResponse>(event).latestJob,
      canRefresh: false,
      nextRefreshAt: ({ event }) =>
        outputOf<RefreshProfileSuggestionsResponse>(event).nextRefreshAt,
      pollCount: 0,
      error: false,
    }),
    setRefreshDeclined: assign({
      latestJob: ({ event }) => outputOf<RefreshProfileSuggestionsResponse>(event).latestJob,
      canRefresh: ({ event }) => !outputOf<RefreshProfileSuggestionsResponse>(event).throttled,
      nextRefreshAt: ({ event }) =>
        outputOf<RefreshProfileSuggestionsResponse>(event).nextRefreshAt,
      pollCount: 0,
      error: false,
    }),
    incrementPollCount: assign({
      pollCount: ({ context }) => context.pollCount + 1,
    }),
    resetPollCount: assign({
      pollCount: 0,
    }),
    setLoadError: assign({
      suggestions: [],
      latestJob: null,
      canRefresh: false,
      nextRefreshAt: null,
      pollCount: 0,
      error: true,
    }),
  },
}).createMachine({
  context: ({ input }) => ({
    providerId: input.providerId || 0,
    featureEnabled: false,
    suggestions: [],
    latestJob: null,
    canRefresh: false,
    nextRefreshAt: null,
    error: false,
    pollCount: 0,
  }),
  initial: 'Idle',
  on: {
    LOAD: {
      target: '.Loading',
      actions: 'prepareLoad',
    },
  },
  states: {
    Idle: {},
    Loading: {
      invoke: {
        src: 'getSuggestions',
        input: ({ context }) => ({ providerId: context.providerId }),
        onDone: [
          {
            target: 'AwaitingRefresh',
            guard: 'shouldKeepPolling',
            actions: ['setSuggestions', 'incrementPollCount'],
          },
          {
            target: 'RefreshFailed',
            guard: 'latestJobFailed',
            actions: ['setSuggestions'],
          },
          {
            target: 'Ready',
            actions: ['setSuggestions', 'resetPollCount'],
          },
        ],
        onError: {
          target: 'Ready',
          actions: 'setLoadError',
        },
      },
    },
    Ready: {
      on: {
        REFRESH: 'Refreshing',
      },
    },
    Refreshing: {
      invoke: {
        src: 'refreshSuggestions',
        input: ({ context }) => ({ providerId: context.providerId }),
        onDone: [
          {
            target: 'AwaitingRefresh',
            guard: 'refreshAccepted',
            actions: 'setRefreshAccepted',
          },
          {
            target: 'Ready',
            actions: 'setRefreshDeclined',
          },
        ],
        onError: {
          target: 'Ready',
          actions: assign({ error: true }),
        },
      },
    },
    AwaitingRefresh: {
      after: {
        5000: 'Loading',
      },
      on: {
        POLL: 'Loading',
      },
    },
    RefreshFailed: {
      on: {
        REFRESH: 'Refreshing',
        POLL: 'Loading',
      },
    },
  },
});

export const selectFeatureEnabled = (state: { context: Context }) => state.context.featureEnabled;
export const selectSuggestions = (state: { context: Context }) => state.context.suggestions;
export const selectCanRefresh = (state: { context: Context }) => state.context.canRefresh;
export const selectLatestJob = (state: { context: Context }) => state.context.latestJob;
export const selectHasError = (state: { context: Context }) => state.context.error;
